import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Card, CardContent, Button } from '@/components/ui'
import { Lock, LogIn, UserPlus, Terminal } from 'lucide-react'

interface RequireAuthProps {
  children: ReactNode
}

export function RequireAuth({ children }: RequireAuthProps) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex items-center gap-3 text-muted-foreground">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          Checking access...
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] p-4">
        <Card className="glass max-w-md w-full">
          <CardContent className="pt-8 pb-8">
            <div className="text-center space-y-6">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-xl bg-primary/10 border border-primary/30">
                <Lock className="h-8 w-8 text-primary" />
              </div>

              <div className="space-y-2">
                <h2 className="text-xl font-bold">Authentication Required</h2>
                <p className="text-sm text-muted-foreground">
                  You need to sign in to access this tool.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/auth">
                  <Button className="w-full sm:w-auto">
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="outline" className="w-full sm:w-auto">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Register
                  </Button>
                </Link>
              </div>

              <div className="text-xs text-muted-foreground font-mono">
                <span className="text-primary">{'>'}</span> Free account required
                <br />
                <span className="text-green-500">$</span> Access encryptors, special tools & more
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return <>{children}</>
}
